import { useState, useEffect, useRef } from "react";
import { Play, Plus, Image, Youtube, Trash2, Calendar, Heart, Shield, Users, Camera, Film, Link, Upload, X, Star, Globe, ExternalLink, Award, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get full image URL - handles all URL formats
const getImageUrl = (url) => {
  if (!url) return '';
  
  // If it's a relative URL, prepend the backend URL
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  
  // If it's an old preview URL, replace with current backend URL
  if (url.includes('.preview.emergentagent.com') || url.includes('.emergent.host')) {
    const path = url.replace(/https?:\/\/[^\/]+/, '');
    return `${BACKEND_URL}${path}`;
  }
  
  // Otherwise return as is (external URLs)
  return url;
};

const keyFacts = [
  { number: "365", label: "Days of service and sacrifice", icon: Shield },
  { number: "24/7", label: "Around the clock protection", icon: Calendar },
  { number: "Jan 9", label: "National Law Enforcement Day", icon: Award },
  { number: "100%", label: "Community dedication", icon: Heart }
];

const aboutPoints = [
  "Police Appreciation Day is dedicated to honoring the men and women who serve and protect our communities every day.",
  "Peace for Change Alliance believes in building positive relationships between law enforcement and the communities they serve.",
  "This initiative celebrates the courage, dedication, and sacrifice of police officers who put their lives on the line.",
  "We promote dialogue, understanding, and collaboration between police departments and community members.",
  "Together, we work toward safer neighborhoods through mutual respect and partnership."
];

export default function PoliceAppreciation() {
  const { isAuthenticated } = useAuth();
  const [videos, setVideos] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [activeTab, setActiveTab] = useState("photos");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [addVideoOpen, setAddVideoOpen] = useState(false);
  const [addPhotoOpen, setAddPhotoOpen] = useState(false);
  const [videoUploadMode, setVideoUploadMode] = useState("youtube");
  const [uploadMode, setUploadMode] = useState("upload");
  const [newVideo, setNewVideo] = useState({ title: "", description: "", youtube_url: "", video_url: "" });
  const [newPhoto, setNewPhoto] = useState({ title: "", description: "", image_url: "" });
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const photoFileInputRef = useRef(null);
  const videoFileInputRef = useRef(null);

  useEffect(() => {
    fetchVideos();
    fetchPhotos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/police/videos`);
      setVideos(response.data);
    } catch (error) {
      console.error("Error fetching videos:", error);
    }
  };

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API}/police/photos`);
      setPhotos(response.data);
    } catch (error) {
      console.error("Error fetching photos:", error);
    }
  };

  const handlePhotoFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 1) {
      setSelectedFile(validFiles[0]);
      setSelectedFiles([]);
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result);
      reader.readAsDataURL(validFiles[0]);
      setPreviewUrls([]);
    } else if (validFiles.length > 1) {
      setSelectedFiles(validFiles);
      setSelectedFile(null);
      setPreviewUrl(null);
      const urls = [];
      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          urls.push(reader.result);
          if (urls.length === validFiles.length) {
            setPreviewUrls([...urls]);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleVideoFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Video is too large. Maximum size is 100MB.");
      return;
    }
    
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const handleUploadPhoto = async (e) => {
    e.preventDefault();
    
    if (uploadMode === "url") {
      if (!newPhoto.image_url) {
        toast.error("Please enter an image URL");
        return;
      }
      setUploading(true);
      try {
        const response = await axios.post(`${API}/police/photos`, {
          title: newPhoto.title || "Police Appreciation Photo",
          description: newPhoto.description || "",
          image_url: newPhoto.image_url
        });
        setPhotos([response.data, ...photos]);
        resetPhotoForm();
        toast.success("Photo added successfully!");
      } catch (error) {
        toast.error("Failed to add photo");
      } finally {
        setUploading(false);
      }
      return;
    }
    
    const filesToUpload = selectedFiles.length > 0 ? selectedFiles : (selectedFile ? [selectedFile] : []);
    
    if (filesToUpload.length === 0) {
      toast.error("Please select at least one photo");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    
    try {
      let successCount = 0;
      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];
        const formData = new FormData();
        formData.append("file", file);
        formData.append("title", newPhoto.title || file.name.split('.')[0]);
        formData.append("description", newPhoto.description || "");
        
        const uploadRes = await axios.post(`${API}/upload/image`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000
        });
        
        const photoData = {
          title: newPhoto.title || file.name.split('.')[0],
          description: newPhoto.description || "",
          image_url: uploadRes.data.url
        };
        
        const response = await axios.post(`${API}/police/photos`, photoData);
        setPhotos(prev => [response.data, ...prev]);
        successCount++;
        setUploadProgress(Math.round(((i + 1) / filesToUpload.length) * 100));
      }
      
      resetPhotoForm();
      toast.success(`${successCount} photo${successCount > 1 ? 's' : ''} uploaded successfully!`);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload photo. Please try again.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleUploadVideo = async (e) => {
    e.preventDefault();
    if (videoUploadMode === "upload" && selectedFile) {
      if (!newVideo.title) {
        toast.error("Please enter a title");
        return;
      }
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("title", newVideo.title);
        formData.append("description", newVideo.description || "");
        
        const uploadRes = await axios.post(`${API}/upload/video`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000
        });
        
        const videoData = {
          title: newVideo.title,
          description: newVideo.description,
          video_url: uploadRes.data.url,
          is_upload: true
        };
        
        const response = await axios.post(`${API}/police/videos`, videoData);
        setVideos([response.data, ...videos]);
        resetVideoForm();
        toast.success("Video uploaded successfully!");
      } catch (error) {
        console.error("Upload error:", error);
        toast.error("Failed to upload video. File may be too large.");
      } finally {
        setUploading(false);
      }
    } else if (videoUploadMode === "url") {
      if (!newVideo.title || !newVideo.video_url) {
        toast.error("Please fill in title and video URL");
        return;
      }
      setUploading(true);
      try {
        const videoData = {
          title: newVideo.title,
          description: newVideo.description,
          video_url: newVideo.video_url,
          is_upload: true
        };
        const response = await axios.post(`${API}/police/videos`, videoData);
        setVideos([response.data, ...videos]);
        resetVideoForm();
        toast.success("Video added successfully!");
      } catch (error) {
        toast.error("Failed to add video");
      } finally {
        setUploading(false);
      }
    } else {
      if (!newVideo.title || !newVideo.youtube_url) {
        toast.error("Please fill in required fields");
        return;
      }
      setUploading(true);
      try {
        const response = await axios.post(`${API}/police/videos`, newVideo);
        setVideos([response.data, ...videos]);
        resetVideoForm();
        toast.success("Video added successfully!");
      } catch (error) {
        toast.error("Failed to add video");
      } finally {
        setUploading(false);
      }
    }
  };

  const resetPhotoForm = () => {
    setNewPhoto({ title: "", description: "", image_url: "" });
    setSelectedFile(null);
    setSelectedFiles([]);
    setPreviewUrl(null);
    setPreviewUrls([]);
    setAddPhotoOpen(false);
    setUploadMode("upload");
    setUploadProgress(0);
  };

  const resetVideoForm = () => {
    setNewVideo({ title: "", description: "", youtube_url: "", video_url: "" });
    setSelectedFile(null);
    setPreviewUrl(null);
    setAddVideoOpen(false);
    setVideoUploadMode("youtube");
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm("Delete this video?")) return;
    try {
      await axios.delete(`${API}/police/videos/${videoId}`);
      setVideos(videos.filter(v => v.id !== videoId));
      toast.success("Video deleted");
    } catch (error) {
      toast.error("Failed to delete video");
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await axios.delete(`${API}/police/photos/${photoId}`);
      setPhotos(photos.filter(p => p.id !== photoId));
      toast.success("Photo deleted");
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A]" data-testid="police-appreciation-page">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-[#1a1a2e] to-[#0F172A]" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djZoNnYtNmgtNnptMC0zMHY2aDZ2LTZoLTZ6bTAgMTJ2Nmg2di02aC02em0wIDEydjZoNnYtNmgtNnptLTEyLTEydjZoNnYtNmgtNnptMCAxMnY2aDZ2LTZoLTZ6bTAtMjR2Nmg2di02aC02em0wIDM2djZoNnYtNmgtNnptLTEyLTI0djZoNnYtNmgtNnptMCAxMnY2aDZ2LTZoLTZ6bTAtMjR2Nmg2di02aC02em0wIDM2djZoNnYtNmgtNnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-600 rounded-2xl mb-6">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Outfit'] text-white mb-6">
              Police Appreciation Day
            </h1>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto mb-8">
              Honoring the courage, dedication, and sacrifice of the men and women who protect and serve our communities every day
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-full text-lg">
                <Heart className="w-5 h-5 mr-2" />
                Show Your Support
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Key Facts */}
      <section className="py-12 bg-[#1a1a2e]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {keyFacts.map((fact, index) => (
              <div key={index} className="text-center p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
                <fact.icon className="w-8 h-8 text-blue-400 mx-auto mb-3" />
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">{fact.number}</div>
                <div className="text-sm text-slate-400">{fact.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-[#16213e]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-white text-center mb-10">
            About This Initiative
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {aboutPoints.map((point, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-colors"
              >
                <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mb-4 text-white font-bold">
                  {index + 1}
                </div>
                <p className="text-slate-300">{point}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Media Gallery */}
      <section className="py-16 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-white">Media Gallery</h2>
              <p className="text-slate-400 mt-1">Photos and videos from Police Appreciation events</p>
            </div>
            <div className="flex gap-3">
              <Dialog open={addVideoOpen} onOpenChange={setAddVideoOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-police-video-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Video
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#16213e] border-slate-700 text-white max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-['Outfit'] text-white">Add Video</DialogTitle>
                  </DialogHeader>
                  
                  <div className="flex flex-wrap gap-2 mt-4">
                    <Button
                      type="button"
                      variant={videoUploadMode === "youtube" ? "default" : "outline"}
                      className={`flex-1 min-w-[100px] ${videoUploadMode === "youtube" ? "bg-blue-600 text-white" : "border-slate-600 text-white"}`}
                      onClick={() => { setVideoUploadMode("youtube"); setSelectedFile(null); setPreviewUrl(null); }}
                    >
                      <Youtube className="w-4 h-4 mr-2" />
                      YouTube
                    </Button>
                    <Button
                      type="button"
                      variant={videoUploadMode === "url" ? "default" : "outline"}
                      className={`flex-1 min-w-[100px] ${videoUploadMode === "url" ? "bg-blue-600 text-white" : "border-slate-600 text-white"}`}
                      onClick={() => { setVideoUploadMode("url"); setSelectedFile(null); setPreviewUrl(null); setNewVideo({ ...newVideo, youtube_url: "" }); }}
                    >
                      <Link className="w-4 h-4 mr-2" />
                      Video URL
                    </Button>
                    <Button
                      type="button"
                      variant={videoUploadMode === "upload" ? "default" : "outline"}
                      className={`flex-1 min-w-[100px] ${videoUploadMode === "upload" ? "bg-blue-600 text-white" : "border-slate-600 text-white"}`}
                      onClick={() => { setVideoUploadMode("upload"); setNewVideo({ ...newVideo, youtube_url: "", video_url: "" }); }}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload
                    </Button>
                  </div>

                  <form onSubmit={handleUploadVideo} className="space-y-4 mt-4">
                    <div>
                      <Label className="text-slate-300">Title *</Label>
                      <Input
                        value={newVideo.title}
                        onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
                        placeholder="Video title"
                        className="mt-1 bg-slate-800 border-slate-600 text-white"
                        required
                      />
                    </div>

                    {videoUploadMode === "youtube" && (
                      <div>
                        <Label className="text-slate-300">YouTube URL *</Label>
                        <Input
                          value={newVideo.youtube_url}
                          onChange={(e) => setNewVideo({ ...newVideo, youtube_url: e.target.value })}
                          placeholder="https://www.youtube.com/watch?v=..."
                          className="mt-1 bg-slate-800 border-slate-600 text-white"
                          required
                        />
                        <p className="text-xs text-slate-400 mt-1">Paste a YouTube video link</p>
                      </div>
                    )}

                    {videoUploadMode === "url" && (
                      <div>
                        <Label className="text-slate-300">Video URL *</Label>
                        <Input
                          value={newVideo.video_url}
                          onChange={(e) => setNewVideo({ ...newVideo, video_url: e.target.value })}
                          placeholder="https://example.com/video.mp4"
                          className="mt-1 bg-slate-800 border-slate-600 text-white"
                          required
                        />
                        <p className="text-xs text-slate-400 mt-1">Direct link to video file (MP4, WebM, etc.) or embed URL</p>
                      </div>
                    )}

                    {videoUploadMode === "upload" && (
                      <div>
                        <Label className="text-slate-300">Select Video *</Label>
                        <input
                          ref={videoFileInputRef}
                          type="file"
                          accept="video/*"
                          onChange={handleVideoFileSelect}
                          className="hidden"
                          id="police-video-upload"
                        />
                        <label 
                          htmlFor="police-video-upload"
                          className="mt-2 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-4 px-6 rounded-xl cursor-pointer font-semibold transition-colors active:bg-blue-800 touch-manipulation"
                        >
                          <Film className="w-5 h-5" />
                          {selectedFile ? "Change Video" : "Choose Video from Phone"}
                        </label>
                        {previewUrl && (
                          <div className="mt-4 p-4 bg-slate-800 rounded-xl">
                            <video src={previewUrl} className="max-h-32 mx-auto rounded-lg" controls />
                            <p className="text-sm text-slate-400 text-center mt-2">{selectedFile?.name}</p>
                            <p className="text-xs text-green-400 text-center mt-1">Video selected</p>
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="sm" 
                              className="w-full mt-2 text-red-400 hover:text-red-300"
                              onClick={() => { setSelectedFile(null); setPreviewUrl(null); }}
                            >
                              Remove
                            </Button>
                          </div>
                        )}
                      </div>
                    )}

                    <div>
                      <Label className="text-slate-300">Description</Label>
                      <Textarea
                        value={newVideo.description}
                        onChange={(e) => setNewVideo({ ...newVideo, description: e.target.value })}
                        placeholder="Brief description"
                        className="mt-1 bg-slate-800 border-slate-600 text-white"
                        rows={3}
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-blue-600 hover:bg-blue-700" 
                      disabled={uploading || (videoUploadMode === "upload" && !selectedFile) || (videoUploadMode === "youtube" && !newVideo.youtube_url) || (videoUploadMode === "url" && !newVideo.video_url)}
                    >
                      {uploading ? "Uploading..." : "Add Video"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={addPhotoOpen} onOpenChange={setAddPhotoOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-blue-500 text-blue-400 hover:bg-blue-500/10" data-testid="add-police-photo-btn">
                    <Camera className="w-4 h-4 mr-2" />
                    Add Photo
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#16213e] border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-['Outfit'] text-white">Add Photo</DialogTitle>
                  </DialogHeader>
                  
                  <div className="flex gap-2 mt-4">
                    <Button
                      type="button"
                      variant={uploadMode === "upload" ? "default" : "outline"}
                      className={`flex-1 ${uploadMode === "upload" ? "bg-blue-600 text-white" : "border-slate-600 text-white"}`}
                      onClick={() => { setUploadMode("upload"); setNewPhoto({ ...newPhoto, image_url: "" }); }}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      From Phone
                    </Button>
                    <Button
                      type="button"
                      variant={uploadMode === "url" ? "default" : "outline"}
                      className={`flex-1 ${uploadMode === "url" ? "bg-blue-600 text-white" : "border-slate-600 text-white"}`}
                      onClick={() => { setUploadMode("url"); setSelectedFile(null); setSelectedFiles([]); setPreviewUrl(null); setPreviewUrls([]); }}
                    >
                      <Link className="w-4 h-4 mr-2" />
                      Image URL
                    </Button>
                  </div>

                  <form onSubmit={handleUploadPhoto} className="space-y-4 mt-4">
                    <div>
                      <Label className="text-slate-300">Title</Label>
                      <Input
                        value={newPhoto.title}
                        onChange={(e) => setNewPhoto({ ...newPhoto, title: e.target.value })}
                        placeholder="Photo title (optional)"
                        className="mt-1 bg-slate-800 border-slate-600 text-white"
                      />
                    </div>

                    {uploadMode === "url" ? (
                      <div>
                        <Label className="text-slate-300">Image URL *</Label>
                        <Input
                          value={newPhoto.image_url}
                          onChange={(e) => setNewPhoto({ ...newPhoto, image_url: e.target.value })}
                          placeholder="https://example.com/image.jpg"
                          className="mt-1 bg-slate-800 border-slate-600 text-white"
                          required
                        />
                      </div>
                    ) : (
                      <div>
                        <Label className="text-slate-300">Select Photo(s) *</Label>
                        <input
                          ref={photoFileInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handlePhotoFileSelect}
                          className="hidden"
                          id="police-photo-upload"
                        />
                        <label 
                          htmlFor="police-photo-upload"
                          className="mt-2 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-4 px-6 rounded-xl cursor-pointer font-semibold transition-colors active:bg-blue-800 touch-manipulation"
                        >
                          <Camera className="w-5 h-5" />
                          {selectedFile || selectedFiles.length > 0 ? "Change Photo(s)" : "Choose Photo(s) from Phone"}
                        </label>
                        
                        {previewUrl && (
                          <div className="mt-4 p-4 bg-slate-800 rounded-xl">
                            <img src={previewUrl} alt="Preview" className="max-h-40 mx-auto rounded-lg object-contain" />
                            <p className="text-sm text-slate-400 text-center mt-2">{selectedFile?.name}</p>
                            <p className="text-xs text-green-400 text-center mt-1">Photo selected</p>
                          </div>
                        )}
                        
                        {previewUrls.length > 0 && (
                          <div className="mt-4 p-4 bg-slate-800 rounded-xl">
                            <div className="grid grid-cols-3 gap-2">
                              {previewUrls.slice(0, 6).map((url, idx) => (
                                <img key={idx} src={url} alt={`Preview ${idx + 1}`} className="w-full h-20 object-cover rounded-lg" />
                              ))}
                            </div>
                            <p className="text-xs text-green-400 text-center mt-2">{selectedFiles.length} photos selected</p>
                          </div>
                        )}
                      </div>
                    )}

                    <div>
                      <Label className="text-slate-300">Description</Label>
                      <Textarea
                        value={newPhoto.description}
                        onChange={(e) => setNewPhoto({ ...newPhoto, description: e.target.value })}
                        placeholder="Brief description"
                        className="mt-1 bg-slate-800 border-slate-600 text-white"
                        rows={3}
                      />
                    </div>

                    {uploading && uploadProgress > 0 && (
                      <div className="w-full bg-slate-700 rounded-full h-2">
                        <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                      </div>
                    )}

                    <Button 
                      type="submit" 
                      className="w-full bg-blue-600 hover:bg-blue-700" 
                      disabled={uploading || (uploadMode === "upload" && !selectedFile && selectedFiles.length === 0) || (uploadMode === "url" && !newPhoto.image_url)}
                    >
                      {uploading ? `Uploading... ${uploadProgress}%` : "Add Photo"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
            <TabsList className="bg-slate-800/50">
              <TabsTrigger value="photos" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                <Image className="w-4 h-4 mr-2" />
                Photos ({photos.length})
              </TabsTrigger>
              <TabsTrigger value="videos" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                <Play className="w-4 h-4 mr-2" />
                Videos ({videos.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="photos" className="mt-6">
              {photos.length === 0 ? (
                <div className="text-center py-12">
                  <Image className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No photos yet. Be the first to add one!</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {photos.map((photo) => (
                    <div 
                      key={photo.id} 
                      className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer"
                      onClick={() => setSelectedPhoto(photo)}
                      data-testid={`police-photo-${photo.id}`}
                    >
                      <img 
                        src={getImageUrl(photo.image_url)} 
                        alt={photo.title} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {photo.is_featured && (
                        <div className="absolute top-2 left-2 bg-yellow-500 text-black px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                          <Star className="w-3 h-3" /> Featured
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <h3 className="text-white font-medium text-sm line-clamp-2">{photo.title}</h3>
                        </div>
                      </div>
                      {isAuthenticated && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute top-2 right-2 bg-red-500/80 hover:bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => { e.stopPropagation(); handleDeletePhoto(photo.id); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="videos" className="mt-6">
              {videos.length === 0 ? (
                <div className="text-center py-12">
                  <Play className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No videos yet. Be the first to add one!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {videos.map((video) => (
                    <div 
                      key={video.id} 
                      className="group relative bg-slate-800/50 rounded-xl overflow-hidden cursor-pointer"
                      onClick={() => setSelectedVideo(video)}
                      data-testid={`police-video-${video.id}`}
                    >
                      <div className="aspect-video relative">
                        {video.is_upload ? (
                          video.video_url?.match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i) ? (
                            <video src={getImageUrl(video.video_url)} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-slate-800 flex flex-col items-center justify-center">
                              <Globe className="w-12 h-12 text-slate-500 mb-2" />
                              <span className="text-xs text-slate-500">External Video</span>
                            </div>
                          )
                        ) : (
                          <img 
                            src={video.thumbnail_url} 
                            alt={video.title}
                            className="w-full h-full object-cover"
                          />
                        )}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
                          <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
                            <Play className="w-8 h-8 text-white ml-1" />
                          </div>
                        </div>
                        {video.is_featured && (
                          <div className="absolute top-2 left-2 bg-yellow-500 text-black px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                            <Star className="w-3 h-3" /> Featured
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-white group-hover:text-blue-400 transition-colors line-clamp-2">
                          {video.title}
                        </h3>
                        {video.description && (
                          <p className="text-sm text-slate-400 mt-1 line-clamp-2">{video.description}</p>
                        )}
                      </div>
                      {isAuthenticated && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute top-2 right-2 bg-red-500/80 hover:bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => { e.stopPropagation(); handleDeleteVideo(video.id); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Photo Viewer Dialog */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="max-w-4xl p-0 bg-[#1a1a2e] border-slate-700 overflow-hidden">
          <DialogTitle className="sr-only">{selectedPhoto?.title}</DialogTitle>
          {selectedPhoto && (
            <div>
              <img 
                src={getImageUrl(selectedPhoto.image_url)} 
                alt={selectedPhoto.title}
                className="w-full max-h-[70vh] object-contain"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-white mb-2">{selectedPhoto.title}</h3>
                {selectedPhoto.description && (
                  <p className="text-slate-400">{selectedPhoto.description}</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Video Player Dialog */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="max-w-4xl p-0 bg-[#1a1a2e] border-slate-700 overflow-hidden">
          <DialogTitle className="sr-only">{selectedVideo?.title}</DialogTitle>
          {selectedVideo && (
            <div>
              {selectedVideo.is_upload ? (
                selectedVideo.video_url?.match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i) ? (
                  <video src={getImageUrl(selectedVideo.video_url)} controls autoPlay className="w-full max-h-[70vh]" />
                ) : (
                  <div className="aspect-video bg-slate-900 flex flex-col items-center justify-center p-8">
                    <Globe className="w-16 h-16 text-slate-500 mb-4" />
                    <p className="text-slate-400 text-center mb-6">This video is hosted on an external website</p>
                    <a 
                      href={selectedVideo.video_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-semibold transition-colors"
                    >
                      <ExternalLink className="w-5 h-5" />
                      Watch on External Site
                    </a>
                  </div>
                )
              ) : (
                <div className="youtube-container">
                  <iframe
                    src={`https://www.youtube.com/embed/${selectedVideo.youtube_id}?autoplay=1`}
                    title={selectedVideo.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}
              <div className="p-6">
                <h3 className="text-xl font-semibold text-white mb-2">{selectedVideo.title}</h3>
                {selectedVideo.description && (
                  <p className="text-slate-400">{selectedVideo.description}</p>
                )}
                {selectedVideo.is_upload && selectedVideo.video_url && !selectedVideo.video_url.match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i) && (
                  <a 
                    href={selectedVideo.video_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 mt-3 text-sm"
                  >
                    {selectedVideo.video_url}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
